import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import ko from 'date-fns/locale/ko';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { Notification } from '../../types/user';
import { useIsMobile } from '../../utils/useMediaQuery';
import {
  deleteAllNotifications,
  deleteNotification,
  deleteNotifications,
  getNotificationTypeColor,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  subscribeToUserNotifications,
} from '../../utils/notifications';
import Button from '../../components/ui/Button';

type TypeFilter = 'all' | Notification['type'];

const TYPE_FILTERS: { id: TypeFilter; label: string }[] = [
  { id: 'all', label: '전체' },
  { id: 'point', label: '포인트' },
  { id: 'approval', label: '승인' },
  { id: 'role', label: '역할' },
  { id: 'item', label: '아이템' },
  { id: 'system', label: '시스템' },
  { id: 'general', label: '일반' },
];

const TYPE_LABELS: Record<Notification['type'], string> = {
  point: '포인트',
  approval: '승인',
  role: '역할',
  item: '아이템',
  system: '시스템',
  general: '일반',
};

// 카드 체크박스 크기와 본문까지의 간격. 아래 버튼 줄도 이만큼 들여 본문 시작선에 맞춘다.
const CARD_CHECKBOX_SIZE = 18;
const CARD_CHECKBOX_GAP = 12;

const formatNotificationTime = (date: Date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return format(date, 'M월 d일 a h:mm', { locale: ko });
};

const MessageInbox: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [deletingSelected, setDeletingSelected] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const unsubscribe = subscribeToUserNotifications(
      currentUser.uid,
      (items) => {
        setNotifications(items);
        setLoading(false);
      },
      100,
      () => {
        setError('알림을 불러오지 못했습니다.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.isRead).length,
    [notifications]
  );

  const visibleNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      if (unreadOnly && notification.isRead) return false;
      if (typeFilter !== 'all' && notification.type !== typeFilter) return false;
      return true;
    });
  }, [notifications, unreadOnly, typeFilter]);

  // 필터로 가려진 알림은 선택돼 있어도 지우지 않는다.
  const selectedVisibleIds = useMemo(
    () => visibleNotifications.filter((notification) => selectedIds.has(notification.id)).map((notification) => notification.id),
    [visibleNotifications, selectedIds]
  );
  const allVisibleSelected =
    visibleNotifications.length > 0 && selectedVisibleIds.length === visibleNotifications.length;

  const toggleSelected = (notificationId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(notificationId)) next.delete(notificationId);
      else next.add(notificationId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(allVisibleSelected ? new Set() : new Set(visibleNotifications.map((notification) => notification.id)));
  };

  const handleMarkAsRead = async (notificationId: string) => {
    if (!currentUser?.uid) return;
    setBusyId(notificationId);
    try {
      await markNotificationAsRead(currentUser.uid, notificationId);
    } catch (markError) {
      console.error('알림 읽음 처리 중 오류:', markError);
      toast.error('읽음 처리에 실패했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (notificationId: string) => {
    if (!currentUser?.uid) return;
    if (!window.confirm('이 알림을 삭제할까요?')) return;
    setBusyId(notificationId);
    try {
      await deleteNotification(currentUser.uid, notificationId);
      toast.success('알림을 삭제했습니다.');
    } catch (deleteError) {
      console.error('알림 삭제 중 오류:', deleteError);
      toast.error('알림 삭제에 실패했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  const handleOpen = async (notification: Notification) => {
    if (!notification.actionUrl) return;
    if (!notification.isRead) {
      await handleMarkAsRead(notification.id);
    }
    navigate(notification.actionUrl);
  };

  const handleMarkAllAsRead = async () => {
    if (!currentUser?.uid || unreadCount === 0) return;
    setMarkingAll(true);
    try {
      await markAllNotificationsAsRead(currentUser.uid);
      toast.success('모든 알림을 읽음 처리했습니다.');
    } catch (markError) {
      console.error('모든 알림 읽음 처리 중 오류:', markError);
      toast.error('모두 읽음 처리에 실패했습니다.');
    } finally {
      setMarkingAll(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!currentUser?.uid || selectedVisibleIds.length === 0) return;
    if (!window.confirm(`선택한 알림 ${selectedVisibleIds.length}개를 삭제할까요?`)) return;
    setDeletingSelected(true);
    try {
      await deleteNotifications(currentUser.uid, selectedVisibleIds);
      toast.success(`알림 ${selectedVisibleIds.length}개를 삭제했습니다.`);
      setSelectedIds(new Set());
    } catch (deleteError) {
      console.error('선택한 알림 삭제 중 오류:', deleteError);
      toast.error('선택 삭제에 실패했습니다.');
    } finally {
      setDeletingSelected(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!currentUser?.uid || notifications.length === 0) return;
    if (!window.confirm('메시지함의 모든 알림을 삭제할까요? 삭제한 알림은 되돌릴 수 없습니다.')) return;
    setDeletingAll(true);
    try {
      const deletedCount = await deleteAllNotifications(currentUser.uid);
      toast.success(`알림 ${deletedCount}개를 모두 삭제했습니다.`);
    } catch (deleteError) {
      console.error('모든 알림 삭제 중 오류:', deleteError);
      toast.error('전체 삭제에 실패했습니다.');
    } finally {
      setDeletingAll(false);
    }
  };

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        padding: isMobile ? '8px 0 32px' : '8px 0 48px',
        fontFamily: 'var(--font-family-primary)',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'flex-end',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '16px',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: isMobile ? '24px' : '28px',
              fontWeight: 600,
              lineHeight: '150%',
              letterSpacing: '-0.02em',
              color: 'var(--Color-text-strong)',
            }}
          >
            메시지함
          </h1>
          <p
            style={{
              margin: '8px 0 0',
              fontSize: '14px',
              lineHeight: '21px',
              color: 'var(--Color-text-assistive, #989898)',
            }}
          >
            포인트, 승인, 역할 등 계정 알림을 확인하고 정리합니다.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span
            style={{
              padding: '8px 12px',
              borderRadius: '999px',
              background: unreadCount > 0 ? '#fff1e7' : 'var(--Color-fill-hover, #F5F5F5)',
              color: unreadCount > 0 ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-assistive, #989898)',
              fontSize: '13px',
              fontWeight: 700,
            }}
          >
            읽지 않음 {unreadCount}개
          </span>
          <Button
            variant="secondary"
            size="small"
            disabled={unreadCount === 0 || markingAll}
            onClick={() => void handleMarkAllAsRead()}
          >
            모두 읽음
          </Button>
          <Button
            variant="outline"
            size="small"
            disabled={notifications.length === 0 || deletingAll}
            onClick={() => void handleDeleteAll()}
          >
            {deletingAll ? '삭제 중...' : '전체 삭제'}
          </Button>
        </div>
      </header>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          alignItems: 'center',
        }}
        role="toolbar"
        aria-label="알림 필터"
      >
        <button
          type="button"
          aria-pressed={unreadOnly}
          onClick={() => setUnreadOnly((prev) => !prev)}
          style={{
            border: unreadOnly ? '1px solid var(--Color-fill-brand-color, #FF6F0F)' : '1px solid var(--Color-line-normal, #F0F0F0)',
            borderRadius: '10px',
            padding: '9px 13px',
            background: unreadOnly ? '#fff1e7' : 'var(--Color-background-normal, white)',
            color: unreadOnly ? '#b54f0b' : 'var(--Color-text-normal, #161616)',
            fontWeight: 700,
            fontSize: '13px',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          안 읽은 것만
        </button>
        {TYPE_FILTERS.map((filter) => {
          const selected = typeFilter === filter.id;
          return (
            <button
              key={filter.id}
              type="button"
              aria-pressed={selected}
              onClick={() => setTypeFilter(filter.id)}
              style={{
                border: selected ? '1px solid var(--Color-fill-brand-color, #FF6F0F)' : '1px solid var(--Color-line-normal, #F0F0F0)',
                borderRadius: '10px',
                padding: '9px 13px',
                background: selected ? '#fff1e7' : 'var(--Color-background-normal, white)',
                color: selected ? '#b54f0b' : 'var(--Color-text-normal, #161616)',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {filter.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div style={{ padding: '48px 0', color: 'var(--Color-text-assistive, #989898)', fontSize: '14px' }}>
          알림을 불러오는 중...
        </div>
      )}

      {!loading && error && (
        <div style={{ padding: '48px 0', color: '#d64545', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {!loading && !error && visibleNotifications.length === 0 && (
        <div
          style={{
            padding: '64px 20px',
            border: '1px dashed var(--Color-line-normal, #F0F0F0)',
            borderRadius: '16px',
            textAlign: 'center',
            color: 'var(--Color-text-assistive, #989898)',
            background: 'var(--Color-background-normal, white)',
          }}
        >
          {notifications.length === 0 ? '받은 알림이 없습니다.' : '조건에 맞는 알림이 없습니다.'}
        </div>
      )}

      {!loading && !error && visibleNotifications.length > 0 && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 16px',
              borderRadius: '12px',
              background: 'var(--Color-fill-hover, #F5F5F5)',
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--Color-text-strong)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleSelectAll}
                style={{ width: '18px', height: '18px', accentColor: 'var(--Color-fill-brand-color, #FF6F0F)', cursor: 'pointer' }}
              />
              전체 선택
              {selectedVisibleIds.length > 0 && (
                <span style={{ color: 'var(--Color-fill-brand-color, #FF6F0F)' }}>
                  {selectedVisibleIds.length}개 선택됨
                </span>
              )}
            </label>
            <Button
              variant="outline"
              size="small"
              disabled={selectedVisibleIds.length === 0 || deletingSelected}
              onClick={() => void handleDeleteSelected()}
            >
              {deletingSelected ? '삭제 중...' : '선택 삭제'}
            </Button>
          </div>
          {visibleNotifications.map((notification) => {
            const busy = busyId === notification.id;
            const checked = selectedIds.has(notification.id);
            return (
              <article
                key={notification.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  padding: '18px 20px',
                  borderRadius: '16px',
                  border: checked
                    ? '1px solid var(--Color-fill-brand-color, #FF6F0F)'
                    : '1px solid var(--Color-line-normal, #F0F0F0)',
                  background: notification.isRead
                    ? 'var(--Color-background-normal, white)'
                    : '#fff8f3',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: `${CARD_CHECKBOX_GAP}px`,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelected(notification.id)}
                    aria-label={`${notification.title} 선택`}
                    style={{
                      width: `${CARD_CHECKBOX_SIZE}px`,
                      height: `${CARD_CHECKBOX_SIZE}px`,
                      margin: '2px 0 0',
                      flexShrink: 0,
                      accentColor: 'var(--Color-fill-brand-color, #FF6F0F)',
                      cursor: 'pointer',
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '999px',
                          background: getNotificationTypeColor(notification.type),
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 700,
                          lineHeight: '18px',
                        }}
                      >
                        {TYPE_LABELS[notification.type] || '알림'}
                      </span>
                      {notification.priority === 'urgent' && (
                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#d64545' }}>긴급</span>
                      )}
                      {!notification.isRead && (
                        <span
                          aria-label="읽지 않음"
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: 'var(--Color-fill-brand-color, #FF6F0F)',
                          }}
                        />
                      )}
                    </div>
                    <h2
                      style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: notification.isRead ? 500 : 700,
                        lineHeight: '24px',
                        color: 'var(--Color-text-strong)',
                      }}
                    >
                      {notification.title}
                    </h2>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '14px',
                        lineHeight: '21px',
                        color: 'var(--Color-text-normal, #161616)',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {notification.message}
                    </p>
                    <div style={{ fontSize: '12px', color: 'var(--Color-text-assistive, #989898)' }}>
                      {formatNotificationTime(notification.createdAt)}
                      {notification.senderName ? ` · ${notification.senderName}` : ''}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    paddingLeft: `${CARD_CHECKBOX_SIZE + CARD_CHECKBOX_GAP}px`,
                  }}
                >
                  {notification.actionUrl && (
                    <Button
                      variant="primary"
                      size="small"
                      disabled={busy}
                      onClick={() => void handleOpen(notification)}
                    >
                      바로가기
                    </Button>
                  )}
                  {!notification.isRead && (
                    <Button
                      variant="outline"
                      size="small"
                      disabled={busy}
                      onClick={() => void handleMarkAsRead(notification.id)}
                    >
                      읽음
                    </Button>
                  )}
                  <Button
                    variant="line"
                    size="small"
                    disabled={busy}
                    onClick={() => void handleDelete(notification.id)}
                  >
                    삭제
                  </Button>
                </div>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
};

export default MessageInbox;
